import React from 'react';
import {
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import { useGetInviteByIdVisitLogsQuery } from '../../redux/residentPortalApi';

interface Props {
  inviteId?: string;
}

/**
 * Per-invite check-in / check-out history, newest first.
 */
const VisitLogTimeline: React.FC<Props> = ({ inviteId }) => {
  const { data: logs, isLoading, isError } = useGetInviteByIdVisitLogsQuery(
    { id: inviteId ?? '' },
    { skip: !inviteId },
  );

  if (!inviteId) return null;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Typography variant="body2" color="error" sx={{ py: 1 }}>
        Failed to load visit history.
      </Typography>
    );
  }

  if (!logs || logs.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
        No check-in/check-out activity recorded yet.
      </Typography>
    );
  }

  return (
    <List dense sx={{ py: 0 }}>
      {logs.map((log: NonNullable<typeof logs>[number]) => (
        <ListItem key={log.id} divider>
          <ListItemIcon sx={{ minWidth: 36 }}>
            {log.action === 'check-in' ? (
              <LoginIcon fontSize="small" color="success" />
            ) : (
              <LogoutIcon fontSize="small" color="info" />
            )}
          </ListItemIcon>
          <ListItemText
            primary={log.action === 'check-in' ? 'Checked In' : 'Checked Out'}
            secondary={log.scannedAt ? new Date(log.scannedAt).toLocaleString('en-GB') : '-'}
          />
        </ListItem>
      ))}
    </List>
  );
};

export default VisitLogTimeline;
